import { useEffect, useRef } from 'react';
import styles from './faq.module.css';

/**
 * Sticky category rail on desktop, horizontally scrollable chips on mobile.
 * Watches the rendered category sections and reports the topmost visible one
 * so the caller can highlight it.
 */
const FaqCategoryNav = ({ categories, activeId, onSelect }) => {
  const chipRowRef = useRef(null);
  const isFirstRender = useRef(true);

  // Keep the active chip in view on mobile as the user scrolls the page.
  // Scrolls only the chip row's own scrollLeft — never scrollIntoView, which
  // would also act on ancestor scrolling boxes (including the viewport) and
  // cancel any in-flight page scroll.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const row = chipRowRef.current;
    if (!row) return;
    // Queried by data attribute, not class: the class name is hashed by CSS
    // Modules, so a literal '.mcl-faq-chip.active' selector would match nothing.
    const activeChip = row.querySelector('[data-active="true"]');
    if (activeChip) {
      const targetLeft = activeChip.offsetLeft - (row.clientWidth - activeChip.clientWidth) / 2;
      row.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
    }
  }, [activeId]);

  const handleSelect = (event, categoryId) => {
    event.preventDefault();
    onSelect(categoryId);
  };

  return (
    <>
      {/* Mobile chips */}
      <div className={styles.chips} ref={chipRowRef} data-testid="faq-category-chips">
        {categories.map((category) => (
          <button
            key={category.id}
            type="button"
            className={`${styles.chip} ${activeId === category.id ? styles.chipActive : ''}`}
            data-active={activeId === category.id}
            onClick={(event) => handleSelect(event, category.id)}
            data-testid={`faq-chip-${category.id}`}
          >
            {category.label}
          </button>
        ))}
      </div>

      {/* Desktop rail */}
      <nav className={styles.rail} aria-label="FAQ categories" data-testid="faq-category-nav">
        {categories.map((category) => (
          <a
            key={category.id}
            href={`#${category.id}`}
            className={`${styles.railItem} ${activeId === category.id ? styles.railItemActive : ''}`}
            aria-current={activeId === category.id ? 'true' : undefined}
            onClick={(event) => handleSelect(event, category.id)}
            data-testid={`faq-nav-${category.id}`}
          >
            {category.label}
          </a>
        ))}
      </nav>
    </>
  );
};

export default FaqCategoryNav;
